import {
  BarChart3,
  Building2,
  CalendarCheck,
  FileText,
  FolderKanban,
  Hammer,
  LayoutDashboard,
  Settings,
  ShieldUser,
  Sparkles,
  Star,
  Users,
} from 'lucide-react';
import { Link, NavLink, Outlet } from 'react-router';
import { useAuth } from '@/auth/AuthContext';
import { useBookingRequests } from '@/hooks/useBookingRequests';
import { cn } from '@/lib/utils';

interface AdminNavItem {
  label: string;
  to: string;
  icon: React.ComponentType<{ className?: string }>;
  // Sections that are part of the CMS vision but not built yet.
  comingSoon?: boolean;
  /** Carries the count of requests awaiting a decision, when there are any. */
  pendingBadge?: boolean;
}

// Mirrors the sections sketched in the original admin.html dashboard.
const navItems: AdminNavItem[] = [
  { label: 'Dashboard', to: '/admin', icon: LayoutDashboard },
  { label: 'Experiences', to: '/admin/experiences', icon: Sparkles },
  { label: 'Featured', to: '/admin/featured', icon: Star },
  { label: 'Collections', to: '/admin/collections', icon: FolderKanban },
  // Requests and bookings live behind one entry: a request is just a booking
  // before it has been decided on.
  { label: 'Bookings', to: '/admin/requests', icon: CalendarCheck, pendingBadge: true },
  { label: 'Invoices', to: '/admin/invoices', icon: FileText },
  // Above Partners: a supplier is who an experience is with, which puts it next
  // to the catalogue rather than next to the companies that book it.
  { label: 'Suppliers', to: '/admin/suppliers', icon: Hammer },
  { label: 'Partners', to: '/admin/partners', icon: Building2 },
  { label: 'Tour Leaders', to: '/admin/guides', icon: Users },
  { label: 'Reports', to: '/admin/reports', icon: BarChart3, comingSoon: true },
  // Sits below the content sections: accounts are about who reaches the CMS
  // rather than what is in it, which puts it next to Settings, not Partners.
  { label: 'Accounts', to: '/admin/accounts', icon: ShieldUser },
  { label: 'Settings', to: '/admin/settings', icon: Settings },
];

/** Layout wrapper for the admin CMS: fixed sidebar + scrollable content. */
export function AdminLayout() {
  const { user, signOutUser } = useAuth();
  // Subscribed here rather than on the dashboard alone: a request waiting for a
  // decision is worth seeing from whichever page you happen to be on.
  const { pendingCount } = useBookingRequests();

  return (
    <div className='tw:flex tw:min-h-screen tw:bg-muted/40'>
      <aside className='tw:sticky tw:top-0 tw:flex tw:h-screen tw:w-64 tw:shrink-0 tw:flex-col tw:bg-ed-black tw:text-ed-cream'>
        <Link
          to='/admin'
          className='tw:flex tw:items-center tw:gap-3 tw:border-b tw:border-ed-umber/30 tw:px-6 tw:py-5'
        >
          <img src='/eightydays_balloon.png' alt='Eighty Days' className='tw:h-10 tw:w-auto tw:object-contain' />
          <div>
            <span className='tw:block tw:font-serif tw:text-lg tw:tracking-widest tw:uppercase'>Eighty Days</span>
            <span className='tw:text-[0.6rem] tw:tracking-[0.3em] tw:uppercase tw:text-ed-gold/70'>
              Admin Portal
            </span>
          </div>
        </Link>

        <nav className='tw:flex tw:flex-1 tw:flex-col tw:py-4'>
          {navItems.map((item) => {
            const Icon = item.icon;
            if (item.comingSoon) {
              return (
                <div
                  key={item.label}
                  className='tw:flex tw:cursor-not-allowed tw:items-center tw:justify-between tw:gap-3 tw:px-6 tw:py-3 tw:text-sm tw:text-ed-cream/40'
                  title='Coming soon'
                >
                  <span className='tw:flex tw:items-center tw:gap-3'>
                    <Icon className='tw:size-4' />
                    {item.label}
                  </span>
                  <span className='tw:rounded tw:bg-ed-umber/30 tw:px-1.5 tw:py-0.5 tw:text-[0.6rem] tw:uppercase'>
                    Soon
                  </span>
                </div>
              );
            }
            return (
              <NavLink
                key={item.label}
                to={item.to}
                end={item.to === '/admin'}
                className={({ isActive }) =>
                  cn(
                    'tw:flex tw:items-center tw:gap-3 tw:border-r-2 tw:border-transparent tw:px-6 tw:py-3 tw:text-base tw:transition-colors',
                    isActive
                      ? 'tw:border-ed-gold tw:bg-ed-gold/15 tw:text-ed-gold'
                      : 'tw:text-ed-cream tw:hover:bg-ed-gold/10 tw:hover:text-ed-gold',
                  )
                }
              >
                <span className='tw:flex tw:items-center tw:gap-3'>
                  <Icon className='tw:size-4' />
                  {item.label}
                </span>
                {item.pendingBadge && pendingCount > 0 && (
                  <span
                    title={`${pendingCount} awaiting a decision`}
                    className='tw:ml-auto tw:min-w-5 tw:rounded-full tw:bg-ed-gold tw:px-1.5 tw:py-0.5 tw:text-center tw:text-[0.65rem] tw:font-semibold tw:text-ed-black'
                  >
                    {pendingCount}
                  </span>
                )}
              </NavLink>
            );
          })}
        </nav>

        <div className='tw:space-y-3 tw:border-t tw:border-ed-umber/30 tw:p-4'>
          <Link to='/' className='tw:block tw:text-xs tw:text-ed-stone tw:hover:text-ed-gold'>
            ← View public site
          </Link>
          {user && (
            <div className='tw:flex tw:items-center tw:justify-between tw:gap-2'>
              <span className='tw:truncate tw:text-xs tw:text-ed-cream/50' title={user.email ?? undefined}>
                {user.displayName || user.email}
              </span>
              <button
                type='button'
                onClick={() => signOutUser()}
                className='tw:shrink-0 tw:text-xs tw:text-ed-stone tw:transition-colors tw:hover:text-ed-gold'
              >
                Sign out
              </button>
            </div>
          )}
        </div>
      </aside>

      <div className='tw:flex-1 tw:overflow-x-clip'>
        <div className='tw:p-8'>
          <Outlet />
        </div>
      </div>
    </div>
  );
}
